import React, { createContext, useCallback, useContext, useEffect, useState, useRef } from "react";
import PropTypes from "prop-types";
import {
  sendPrompt,
  getAIEntity,
  createThread,
  getNpcComponent,
  sendThreadPrompt,
  getAiEntities
} from "../../utils/openAI-utils";
import { upload } from "../../utils/media-utils";
import aiAgents from "../../singleton/AIAgents";

let uniqueMessageId = 0;
let uniqueThreadId = 0;

export const PrivateChatContext = createContext({ messageGroups: [], sendMessage: () => {} });

function processPrivateChatMessage(privateMessageGroups, newMessage, mySessionId) {
  const now = Date.now();
  const { name, sent, sender, sessionId, ...messageProps } = newMessage;

  // Find the thread for this sender and receiver
  // const threadId = `${receiver}`;
  // let thread = privateMessageGroups.find(group => group.id === threadId);
  let privateMessageGroup = {
    id: uniqueThreadId++,
    timestamp: now,
    sent: mySessionId === messageProps?.body?.senderSessionId,
    sender: mySessionId === messageProps?.body?.senderSessionId ? "You" : messageProps?.body?.sender,
    senderSessionId: messageProps?.body?.senderSessionId || sessionId,
    receiver: messageProps?.body?.receiver || receiver,
    messages: [],
    type: newMessage.type,
    loading: messageProps?.body?.loading
  };
  privateMessageGroups.push(privateMessageGroup);
  // Add the new message to the thread
  privateMessageGroup.messages.push({
    id: uniqueMessageId++,
    timestamp: now,
    body: messageProps?.body?.message || messageProps?.body,
    type: newMessage.type,
    play: messageProps?.body?.play,
    loadingAudio: messageProps?.body?.loadingAudio,
    read: mySessionId === messageProps?.body?.senderSessionId,
    playable: messageProps.body.playable
  });

  return [...privateMessageGroups];
}

function processEditPrivateMessage(privateMessageGroups, newMessage, receiver, messageId) {
  const { name, sent, sender, sessionId, groupId, ...messageProps } = newMessage;
  let messageGroupId = groupId !== undefined && groupId !== null ? groupId : newMessage?.body?.groupId;
  if (messageGroupId !== undefined && messageGroupId !== null) {
    return [
      ...privateMessageGroups.map(group => {
        if (group.id === messageGroupId) {
          group.messages[0].body = messageProps?.body?.message || group.messages[0].body;
          group.loading = messageProps?.body?.loading;
          // group.play = messageProps?.body?.play;
          group.messages[0].play = messageProps?.body?.play || group.messages[0].play;
          group.messages[0].audio = messageProps?.body?.audio || group.messages[0].audio;
          group.messages[0].playable = messageProps?.body?.playable ?? group.messages[0].playable;
          group.messages[0].loadingAudio = messageProps?.body?.loadingAudio;
        }

        return { ...group };
      })
    ];
  }

  // return [
  //   ...privateMessageGroups.map(group => {
  //     if (group.id === uniqueThreadId - 1) {
  //       group.messages[0].body = messageProps?.body?.message;
  //       group.loading = messageProps?.body?.loading;
  //       // group.play = messageProps?.body?.play;
  //       group.messages[0].play = messageProps?.body?.play;
  //       group.messages[0].audio = messageProps?.body?.audio;
  //     }

  //     return { ...group };
  //   })
  // ];
}

function addMessage({ newMessage, setPrivateMessageGroups, mySessionId, setUnreadMessages }) {
  // evaluate socket or local message
  const type = newMessage?.body?.privacyType;
  const sessionId = mySessionId || window.mySessionId;

  if (type == "private-chat" && newMessage?.body?.receiver === sessionId) {
    setUnreadMessages(true);
  }

  switch (type) {
    case "private-chat":
      return setPrivateMessageGroups(messages => processPrivateChatMessage(messages, newMessage, mySessionId));
  }
}

function editMessage({ newMessage, setPrivateMessageGroups, receiver, thread, messageId, groupId }) {
  switch (newMessage.type) {
    case "private-chat":
      return setPrivateMessageGroups(messages =>
        processEditPrivateMessage(messages, newMessage, receiver, thread, messageId, groupId)
      );
  }
}

function removeMessageGroup({ id, setPrivateMessageGroups }) {
  setPrivateMessageGroups(groups => deletePrivateMessageGroup(groups, id));
}

function deletePrivateMessageGroup(privateMessageGroups, id) {
  return privateMessageGroups.filter(group => group.id !== id);
}

function cancelPlayPrivateMessages(setPrivateMessageGroups) {
  return setPrivateMessageGroups(
    messageGroups =>
      (messageGroups = messageGroups.map(messageGroup => {
        messageGroup.messages = messageGroup.messages.map(message => {
          message.play = false;
          message.loadingAudio = false;
          return message;
        });
        return messageGroup;
      }))
  );
}

export function PrivateChatContextProvider({ messageDispatch, children, mySessionId }) {
  const [privateMessageGroups, setPrivateMessageGroups] = useState([]);
  const [receiver, setReceiver] = useState(null);
  const [thread, setOpenAIThread] = useState(null);
  const [tabs, setTabs] = useState(["PUBLIC", "PRIVATE"]);
  const [aiIds, setAiIds] = useState([]);
  const [chatUser, setChatUser] = useState(null);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [sendingAIMessage, setSendingAIMessage] = useState(null);

  const privateMessagesRef = useRef();
  useEffect(() => {
    privateMessagesRef.current = privateMessageGroups;
  }, [privateMessageGroups]);

  const cancelAllPlay = useCallback(() => {
    cancelPlayPrivateMessages(setPrivateMessageGroups);
    const agent = aiAgents.getAgentById(receiver);
    aiAgents.cancelAllPlay(agent);
  }, [setPrivateMessageGroups, receiver]);

  async function createNPCThread(npcAiComponent) {
    const threadId = await createThread(npcAiComponent?.apiKey);
    setOpenAIThread(threadId);
    npcAiComponent.threadId = threadId;
  }

  const sendMessageSpawn = useCallback(
    async (file, receiver, sessionId) => {
      if (messageDispatch) {
        const response = await upload(file, "image/png");

        window.APP.store.update({
          uploadPromotionTokens: [{ fileId: response.file_id, promotionToken: response.meta.promotion_token }]
        });
        const url = response.origin + `?token=${response.meta.access_token}`;
        const me = window?.APP?.store?.state?.profile.displayName;
        let eventType = "object";

        if (file?.type?.startsWith("image/")) {
          eventType = "photo";
        } else if (file?.type?.startsWith("video/")) {
          eventType = "video";
        } else if (file?.type?.startsWith("audio/")) {
          eventType = "audio";
        }

        messageDispatch.push(
          {
            name: file.name,
            src: url,
            canSpawn: true,
            receiver,
            sender: me,
            sent: true,
            senderSessionId: sessionId,
            privacyType: "private-chat"
          },
          eventType
        );
      }
    },
    [messageDispatch]
  );

  useEffect(() => {
    function onReceiveMessage(event) {
      const newMessage = event.detail;
      addMessage({ newMessage, setPrivateMessageGroups, receiver, thread, mySessionId, setUnreadMessages });
    }

    function onEditMessage(event) {
      const newMessage = event.detail;
      const { messageId, groupId } = newMessage;
      editMessage({ newMessage, setPrivateMessageGroups, receiver, thread, messageId });
    }

    function onRemoveMessage(event) {
      const newMessage = event.detail;
      const { id } = newMessage;
      removeMessageGroup({ id, setPrivateMessageGroups });
    }

    function onCancelPlay() {
      cancelPlayPrivateMessages(setPrivateMessageGroups);
    }

    if (messageDispatch) {
      messageDispatch.mySessionId = mySessionId;
      messageDispatch.addEventListener("message", onReceiveMessage);
      messageDispatch.addEventListener("ai-local-message", onReceiveMessage);
      messageDispatch.addEventListener("ai-local-message-edit", onEditMessage);
      messageDispatch.addEventListener("ai-cancel-play", onCancelPlay);
      messageDispatch.addEventListener("ai-remove-message", onRemoveMessage);
    }

    return () => {
      if (messageDispatch) {
        messageDispatch.removeEventListener("message", onReceiveMessage);
        messageDispatch.removeEventListener("ai-local-message", onReceiveMessage);
        messageDispatch.removeEventListener("ai-local-message-edit", onEditMessage);
        messageDispatch.removeEventListener("ai-cancel-play", onCancelPlay);
        messageDispatch.removeEventListener("ai-remove-message", onRemoveMessage);
      }
    };
  }, [messageDispatch, setPrivateMessageGroups, receiver, thread, mySessionId]);

  const sendMessage = useCallback(
    message => {
      if (messageDispatch) {
        messageDispatch.dispatch(message);
      }
    },
    [messageDispatch]
  );

  const sendPrivateMessage = useCallback(
    async (message, receiver, sessionId) => {
      if (messageDispatch) {
        const me = window?.APP?.store?.state?.profile.displayName;
        messageDispatch.push(
          { message, receiver, sender: me, sent: true, senderSessionId: sessionId, privacyType: "private-chat" },
          "chat"
        );
      }
    },
    [messageDispatch]
  );

  const sendAIPrivateMessage = useCallback(
    async (message, receiver, sessionId = "") => {
      if (messageDispatch) {
        const agent = aiAgents.getAgentById(receiver);
        const aiEntity = agent?.el;
        const npcComponent = getNpcComponent(aiEntity);
        const type = "private-chat";

        messageDispatch.dispatchEvent(
          new CustomEvent("ai-local-message", {
            detail: {
              type,
              body: {
                message,
                receiver,
                sender: "You",
                sent: true,
                privacyType: "private-chat",
                senderSessionId: sessionId
              }
            }
          })
        );

        aiAgents.setAILoadingMessage(receiver);
        try {
          setSendingAIMessage(true);
          await new Promise(resolve => setTimeout(resolve, 100));
          const AILastMessage = getAILastMessage(receiver, true);
          await aiAgents.sendPrompt({ agent, message, enableSpeech: false, messageId: AILastMessage.id });
          setSendingAIMessage(false);
        } catch (e) {
          new CustomEvent("ai-message", {
            detail: { type: "private-chat", body: "There was an error sending the message", receiver }
          });
          console.error(e);
          setSendingAIMessage(false);
        }
      }
    },
    [messageDispatch]
  );

  const readAllMesages = useCallback(
    receiver => {
      return setPrivateMessageGroups(
        messageGroups =>
          (messageGroups = messageGroups.map(messageGroup => {
            messageGroup.messages = messageGroup.messages.map(message => {
              if (messageGroup.receiver === mySessionId && messageGroup.senderSessionId === receiver) {
                message.read = true;
              }
              return message;
            });
            return messageGroup;
          }))
      );
    },
    [setPrivateMessageGroups, mySessionId]
  );

  const getAILastMessage = (receiver, loading = false) => {
    let groupMessages = privateMessagesRef.current.filter(messageGroup => messageGroup.receiver == receiver);
    if (loading) {
      groupMessages = groupMessages.filter(group => group.loading === loading);
    }
    const message = groupMessages[groupMessages.length - 1];
    return message;
  };

  useEffect(() => {
    window.getAILastMessage = getAILastMessage;
    // returns {loading, messages[], receiver, sender}
    // each message contains {id,audio, play,read, type: "private-chat", body: {message,receiver,sender,loading, privacyType}}
  }, []);

  return (
    <PrivateChatContext.Provider
      value={{
        sendMessage,
        setPrivateMessageGroups,
        privateMessageGroups,
        sendPrivateMessage,
        setReceiver,
        receiver,
        thread,
        setOpenAIThread,
        createNPCThread,
        cancelAllPlay,
        tabs,
        setTabs,
        chatUser,
        setChatUser,
        sendMessageSpawn,
        sendAIPrivateMessage,
        privateUnreadMessages: unreadMessages,
        setPrivateUnreadMessages: setUnreadMessages,
        aiIds,
        setAiIds,
        readAllMesages,
        sendingAIMessage,
        getAILastMessage
      }}
    >
      {children}
    </PrivateChatContext.Provider>
  );
}

PrivateChatContextProvider.propTypes = {
  children: PropTypes.node,
  messageDispatch: PropTypes.object
};
